import * as React from 'react';
import {useMemo, useRef, createContext, useContext} from 'react';
import {
  GridKeyboardDelegate,
  useGridCell,
  useGridRow,
  useGrid,
  GridCollection,
  useGridState,
} from '@sail/react-aria';

import type {GridState} from '@sail/react-aria';
import {provider, assignProps, createViewConfig, view} from '@sail/engine';

import type {View} from '../view';
import {
  useCollection,
  useItem,
  CollectionProvider,
  useListState,
} from '../deprecated/components/collections';

import type {
  ActionCallback,
  CollectionItemId,
} from '../deprecated/components/collections/types';
import {css} from '../css';
import {tokens} from '../tokens';

type GridItem = Record<string, unknown>;
interface ListContextValue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state?: GridState<GridItem, any>;
  onAction?: ActionCallback;
}

const ListContext = createContext<ListContextValue>({});

function useListContext(): ListContextValue {
  return useContext(ListContext);
}

type Focusable = Omit<React.DOMAttributes<Element>, 'children'>;

interface ItemProps
  extends Omit<
    View.IntrinsicElement<'div'>,
    'title' | 'id' | 'key' | 'children'
  > {
  /**
   * Size of the ListItem component
   * @external
   */
  size?: 'default' | 'large';

  /**
   * The value to display on the right-hand side of the item
   * @external
   */
  value?: React.ReactNode;

  /**
   * The id of the item. This will be passed into the `onAction` handler of `List`
   * @external
   */
  id?: CollectionItemId;

  /**
   * Title content for the ListItem component
   * @external
   */
  title?: React.ReactNode;

  /**
   * Secondary content for the ListItem component
   * @external
   */
  secondaryTitle?: React.ReactNode;

  /**
   * Icon that appears to the left of the content and description. Will be overridden by `image` if both are present.
   * @external
   */
  icon?: React.ReactNode;

  /**
   * Image that appears to the left of the content and description. Will override `icon` if both are present.
   * @external
   */
  image?: React.ReactNode;

  /**
   * Event handler called when the item is pressed
   * @external
   */
  onAction?: () => void;

  subviews?: View.Subviews<{
    image: 'div';
    title: 'div';
    secondaryTitle: 'div';
    value: 'div';
  }>;
}

export const ListItemConfig = createViewConfig({
  name: 'ListItem',
  props: {} as ItemProps,
  flattens: true,
  defaults: {
    size: 'default',
  },
});

/** @external */
export const ListItem = ListItemConfig.createView(
  (props) => {
    const {key} = useItem({
      'aria-label':
        typeof props.title === 'string'
          ? props.title
          : props['aria-label'] || '',
    });

    const cellReference = useRef<HTMLDivElement>(null);
    const {
      title,
      secondaryTitle,
      icon,
      image,
      value,
      size,
      id,
      onAction,
      subviews,
      ...restProps
    } = props;
    const {state, onAction: onListAction} = useListContext();

    if (state == null) {
      throw new Error('<ListItem> rendered outside <List>');
    }

    const actionHandler =
      onAction || (onListAction ? () => onListAction(id || key) : undefined);

    const ref = useRef(null);
    const {rowProps} = useGridRow(
      {
        node: state.collection.getItem(key) || {key},
        isVirtualized: false,
        onAction: actionHandler,
      },
      state,
      ref,
    );

    const {gridCellProps} = useGridCell(
      {
        node: state.collection.getItem(`cell-${key}`) || {key: `cell-${key}`},
        focusMode: 'cell',
      },
      state,
      cellReference,
    );

    const actionStyles = css(
      actionHandler
        ? {
            cursor: 'pointer',
            ':hover': {
              backgroundColor: 'offset',
            },
          }
        : {},
    );

    return (
      <view.div
        {...restProps}
        ref={ref}
        uses={[
          assignProps({
            ...(rowProps as Focusable),
            'aria-disabled':
              restProps['aria-disabled'] ?? rowProps['aria-disabled'],
          }),
          actionStyles,
        ]}
      >
        {/* item cell container */}
        <view.div
          css={{
            stack: 'x',
            alignY: 'center',
            width: 'fill',
            distribute: 'space-between',
            focusRing: 'none',
          }}
          uses={[
            assignProps({ref: cellReference}),
            assignProps(gridCellProps as Focusable),
          ]}
        >
          {/* left content */}
          <view.div
            css={{
              overflow: 'hidden',
              stack: 'x',
              gap: 'small',
              maxWidth: value ? '2/3' : 'fill',
              alignY: 'center',
            }}
          >
            {/* left content: image (optional) */}
            {icon ?? image ? (
              <view.div
                inherits={subviews.image}
                css={
                  // large style list item? OK, a lovely gray box for you!
                  size === 'large'
                    ? {
                        minWidth: '40px',
                        height: '40px',
                        backgroundColor: tokens.color.hue.gray[50],
                        borderRadius: 'xsmall',
                      }
                    : {}
                }
              >
                {icon ?? image}
              </view.div>
            ) : null}
            {/* left content: text */}
            <view.div
              css={{
                stack: 'y',
                overflow: 'hidden',
              }}
            >
              {/* left content: text: title */}
              <view.div
                inherits={subviews.title}
                css={{
                  font: secondaryTitle
                    ? 'label.medium.emphasized'
                    : 'label.medium',
                }}
              >
                {title}
              </view.div>
              {/* left content: text: secondaryTitle (optional) */}
              {secondaryTitle ? (
                <view.div inherits={subviews.secondaryTitle}>
                  {secondaryTitle}
                </view.div>
              ) : null}
            </view.div>
          </view.div>
          {/* right content (optional) */}
          {value ? (
            <view.div
              inherits={subviews.value}
              css={{maxWidth: '1/3', stack: 'x'}}
            >
              {value}
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      padding: 'small',
      borderColor: 'border',
      borderBottomStyle: 'solid',
      borderBottomWidth: 'small',

      ':focus-within': {
        keyline: 'transparent',
        focusRing: 'focus',
      },
    },
    subviews: {
      image: {
        css: {width: 'fit', stack: 'x', alignX: 'center', alignY: 'center'},
      },
      title: {
        css: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      secondaryTitle: {
        css: {
          font: 'label.small',
          textColor: 'subdued',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      value: {
        css: {
          font: 'body.small',
          alignX: 'end',
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
  },
);

type ListProps = View.IntrinsicElement<
  'div',
  {
    /**
     * Press event handler which receives the corresponding key of the list item that was pressed
     * @external
     */
    onAction?: ActionCallback;
  }
>;

export const ListConfig = createViewConfig({
  name: 'List',
  props: {} as ListProps,
  flattens: true,
});

/** @external */
export const List = ListConfig.createView(
  ({children, inherits, onAction, ...rest}) => {
    const ref = useRef(null);
    const collection = useCollection(children);
    const listState = useListState({getItems: collection.getItems});

    const gridCollection = useMemo(
      () =>
        new GridCollection({
          columnCount: 1,
          items: [...listState.collection].map((item) => ({
            ...item,
            hasChildNodes: true,
            childNodes: [
              {
                key: `cell-${item.key}`,
                type: 'cell',
                index: 0,
                value: null,
                level: 0,
                rendered: null,
                textValue: item.textValue,
                hasChildNodes: false,
                childNodes: [],
              },
            ],
          })),
        }),
      [listState.collection],
    );

    const state = useGridState({
      ...rest,
      collection: gridCollection as unknown as GridCollection<GridItem>,
      focusMode: 'cell',
      selectionMode: 'none',
    });

    const keyboardDelegate = useMemo(
      () =>
        new GridKeyboardDelegate({
          collection: state.collection,
          disabledKeys: state.disabledKeys,
          ref,
          direction: 'ltr',

          // Focus the List cell instead of the row so that focus doesn't change with left/right arrow keys when there aren't any
          // focusable children in the cell.
          focusMode: 'cell',
        }),
      [state, ref],
    );

    const {gridProps} = useGrid({keyboardDelegate, ...rest}, state, ref);

    return (
      <view.div
        inherits={inherits}
        ref={ref}
        uses={[
          assignProps(gridProps as Focusable),
          provider(ListContext, {
            state,
            onAction,
          }),
        ]}
      >
        <CollectionProvider collection={collection}>
          {collection.children}
        </CollectionProvider>
      </view.div>
    );
  },
);
